  <template>
  <!-- CRM setup for a pending aftercare record: enter the cadence, the
       turn-over/delivery date, and whether follow-ups are on-site or a call.
       Saving starts the schedule (recurring for installs & device purchases,
       a single follow-up for repairs). -->
  <v-dialog v-model="show" width="560" persistent scrollable>
    <v-card>
      <ModalHeader
        title="Set Up Schedule"
        icon="mdi-calendar-plus"
        :subtitle="headerSubtitle"
        @close="close"
      />
      <v-card-text class="pa-4" v-if="system">
        <v-alert dense text type="info" class="mb-4">
          {{ intro }}
        </v-alert>

        <div class="section-label">
          <v-icon x-small>mdi-tag</v-icon> Category &amp; Type
        </div>
        <v-row dense>
          <v-col cols="12" sm="6">
            <v-select
              v-model="form.category"
              :items="categoryItems"
              label="Category"
              outlined
              dense
              hide-details
              @change="onCategoryChange"
            />
          </v-col>
          <v-col cols="12" sm="6">
            <v-combobox
              v-model="form.system_type"
              :items="subtypeItems"
              label="Type"
              outlined
              dense
              hide-details
            />
          </v-col>
          <v-col cols="12">
            <v-text-field
              v-model="form.end_user_contact"
              label="End User Contact Number"
              hint="Person on site to call for follow-ups (esp. for government accounts)"
              persistent-hint
              prepend-inner-icon="mdi-phone-account"
              outlined
              dense
            />
          </v-col>
        </v-row>

        <div class="section-label mt-3">
          <v-icon x-small>mdi-tune</v-icon> Schedule
        </div>
        <v-row dense>
          <v-col cols="12" sm="6">
            <v-text-field
              v-model="form.installed_on"
              :label="dateLabel"
              type="date"
              :rules="[(v) => !!v || 'Required']"
              outlined
              dense
              class="invoice-date-input"
            />
          </v-col>
          <v-col cols="12" sm="6">
            <v-text-field
              v-model.number="form.maintenance_cadence_months"
              :label="isRepair ? 'Follow-up after (months)' : 'Every (months)'"
              type="number"
              min="1"
              max="24"
              :rules="[(v) => (!!v && v >= 1) || 'Required']"
              outlined
              dense
            />
          </v-col>
          <v-col cols="12" sm="6" v-if="!isRepair">
            <v-text-field
              v-model.number="form.max_visits"
              label="Number of visits (end)"
              type="number"
              min="1"
              max="24"
              hint="Schedule ends after this many visits"
              persistent-hint
              outlined
              dense
            />
          </v-col>
          <v-col cols="12" :sm="isRepair ? 12 : 6">
            <v-select
              v-model="form.visit_mode"
              :items="modeItems"
              label="Mode"
              outlined
              dense
              hide-details
            />
          </v-col>
        </v-row>
        <p class="caption grey--text mt-2 mb-0">
          {{ modeHint }}
        </p>
      </v-card-text>
      <v-divider />
      <v-card-actions class="px-4 py-3">
        <span class="caption grey--text">aftercareSetupModal.vue</span>
        <v-spacer />
        <v-btn text @click="close">Cancel</v-btn>
        <v-btn color="primary" depressed :loading="saving" @click="save">
          Start Schedule
        </v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>

<script>
import { mapActions } from "vuex";

export default {
  name: "AftercareSetupModal",
  props: {
    value: { type: Boolean, default: false },
    system: { type: Object, default: null },
  },
  data() {
    return {
      saving: false,
      modeItems: [
        { text: "Onsite", value: "onsite" },
        { text: "Call (remote)", value: "remote" },
      ],
      categoryItems: [
        { text: "Installed System", value: "installed_system" },
        { text: "Device Purchase", value: "device_purchase" },
        { text: "Repair", value: "repair" },
      ],
      subtypesByCategory: {
        installed_system: ["CCTV", "Starlink", "Solar", "Network", "Server"],
        device_purchase: ["Laptop", "Desktop", "Printer"],
        repair: ["Laptop", "Desktop", "Printer", "CCTV", "Other"],
      },
      form: {
        category: "installed_system",
        system_type: "",
        end_user_contact: "",
        installed_on: new Date().toISOString().substr(0, 10),
        maintenance_cadence_months: 3,
        max_visits: 4,
        visit_mode: "onsite",
      },
    };
  },
  computed: {
    show: {
      get() {
        return this.value;
      },
      set(v) {
        this.$emit("input", v);
      },
    },
    category() {
      return this.form.category || "installed_system";
    },
    subtypeItems() {
      return this.subtypesByCategory[this.form.category] || [];
    },
    isRepair() {
      return this.category === "repair";
    },
    isInstall() {
      return this.category === "installed_system";
    },
    headerSubtitle() {
      if (!this.system) return "";
      const name =
        (this.system.customer && this.system.customer.customer_name) || "";
      return `${this.system.system_type} — ${name}`;
    },
    dateLabel() {
      return this.isInstall
        ? "Completion / Turn-over date"
        : "Delivery / Pickup date";
    },
    intro() {
      if (this.isRepair) {
        return "Repairs get a single follow-up (one call or visit) after the set interval from the completion date.";
      }
      return "Enter the cadence and the turn-over/delivery date. The schedule runs every N months for the set number of visits, then ends.";
    },
    modeHint() {
      if (this.form.visit_mode === "onsite") {
        return "Onsite: each visit lands on the Field Order calendar as a Maintenance job.";
      }
      return "Call: each follow-up is a remote check (phone/online) and stays off the Field Order calendar.";
    },
  },
  watch: {
    value(v) {
      if (v) this.load();
    },
  },
  methods: {
    ...mapActions({
      setup_system: "installed_system/setup_system",
    }),
    load() {
      const s = this.system || {};
      this.form = {
        category: s.category || "installed_system",
        system_type: s.system_type || "",
        end_user_contact: s.end_user_contact || "",
        // Prefer an already-known date; else today.
        installed_on: s.installed_on
          ? String(s.installed_on).substr(0, 10)
          : new Date().toISOString().substr(0, 10),
        maintenance_cadence_months: s.maintenance_cadence_months || 3,
        max_visits: s.max_visits || 4,
        visit_mode:
          s.visit_mode && s.visit_mode !== "alternating"
            ? s.visit_mode
            : "onsite",
      };
    },
    // When the category changes, snap the type to that category's first option
    // if the current value doesn't belong to it.
    onCategoryChange(cat) {
      const list = this.subtypesByCategory[cat] || [];
      if (!list.includes(this.form.system_type)) {
        this.form.system_type = list[0] || "";
      }
    },
    async save() {
      if (!this.form.installed_on || !this.form.maintenance_cadence_months) {
        this.$alert({
          title: "Missing fields",
          message: "Enter the date and the cadence.",
          color: "warning",
          icon: "mdi-alert",
        });
        return;
      }
      this.saving = true;
      try {
        const payload = {
          category: this.form.category,
          system_type: this.form.system_type,
          end_user_contact: this.form.end_user_contact,
          installed_on: this.form.installed_on,
          maintenance_cadence_months: Number(this.form.maintenance_cadence_months),
          visit_mode: this.form.visit_mode,
        };
        if (!this.isRepair) payload.max_visits = Number(this.form.max_visits);
        await this.setup_system({ id: this.system.id, payload });
        this.$alert({
          title: "Schedule started",
          message: "The maintenance schedule has been set up.",
          color: "success",
          icon: "mdi-check-circle",
        });
        this.$emit("saved");
        this.close();
      } catch (e) {
        this.$alert({
          title: "Error",
          message:
            (e.response && e.response.data && (e.response.data.error || e.response.data.message)) ||
            "Failed to set up the schedule.",
          color: "error",
          icon: "mdi-alert",
        });
      } finally {
        this.saving = false;
      }
    },
    close() {
      this.show = false;
    },
  },
};
</script>
